<template>
    <DefaultHeader />

    <!-- Overlay global pendant suppression historique -->
    <div v-if="isDeleting" class="overlay">
        <div class="text-center text-light">
            <div class="spinner-border spinner-border-lg mb-3" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="fs-5">
                Suppression en cours…<br>
                Cette opération peut durer plusieurs dizaines de secondes voire quelques minutes selon la quantité de
                données.
            </p>
            <p>Vous pouvez quitter la page, le traitement continuera en arrière-plan.</p>
        </div>
    </div>

    <div class="container mt-5">
        <div class="card shadow">
            <div
                class="card-header bg-primary text-white d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center py-3">
                <h3 class="mb-2 mb-md-0">Éditer la balise</h3>

                <!-- Le bloc client reste sur une ligne -->
                <div class="text-md-end">
                    <span>
                        Client : {{ balise?.client.name }}
                        <font-awesome-icon class="ms-2"
                            :icon="clientLocked ? ['fas', 'fa-lock'] : ['fas', 'fa-unlock']" />
                        <router-link :to="`/clientpage`" class="text-white fst-italic ms-1">
                            (<span :class="'text-decoration-underline'">{{ clientLocked ? 'Verrouillé' :
                                'Déverrouillé' }}</span>)
                        </router-link>
                    </span>
                </div>
            </div>



            <div class="card-body" v-if="balise">
                <form @submit.prevent="updateBalise">
                    <!-- IMEI et Téléphone -->
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <label for="imei" class="form-label fw-bold">IMEI</label>
                            <input v-model="balise.baliseId" type="text" class="form-control"
                                :class="{ 'locked-field': clientLocked }" id="imei" required inputmode="numeric"
                                placeholder="IMEI ou identifiant"
                                :disabled="isUpdating || isDeleting || clientLocked" />
                            <small v-if="isClientLocked" class="text-danger fst-italic">
                                Champ verrouillé car le client est verrouillé
                            </small>
                        </div>
                        <div class="col-md-6">
                            <label for="tel" class="form-label fw-bold">Téléphone</label>
                            <input v-model="balise.simNumber" type="text" class="form-control" id="tel"
                                pattern="^\+?\d{8,15}$" placeholder="Numéro de la carte sim" maxlength="16"
                                :disabled="isUpdating || isDeleting" />
                        </div>
                    </div>

                    <!-- Nom et Type de balise -->
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <label for="name" class="form-label fw-bold">Nom</label>
                            <input v-model="balise.name" type="text" class="form-control" id="name"
                                :disabled="isUpdating || isDeleting" />
                        </div>
                        <div class="col-md-6">
                            <label for="type" class="form-label fw-bold">Type de balise</label>
                            <v-select v-model="balise.type" :options="baliseOptions" label="label"
                                :reduce="b => b.value" :disabled="isUpdating || isDeleting || clientLocked" required />
                            <small v-if="baliseServerInfo" class="text-muted d-flex justify-content-start fst-italic">
                                <div class="mx-2"><strong>Serveur :</strong> {{ ipAddressBaliseManager }}</div>
                                <div class="mx-2"><strong>Port :</strong> {{ portsByBaliseType[balise.type] }}</div>
                            </small>
                        </div>
                    </div>

                    <!-- Client et Forfait -->
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <label for="client" class="form-label fw-bold">Client</label>
                            <v-select v-model="balise.clientId" :options="clients" label="name"
                                :reduce="client => client.id" :disabled="isUpdating || isDeleting || clientLocked"
                                required />
                        </div>
                        <div class="col-md-6">
                            <label for="package" class="form-label fw-bold">Forfait</label>
                            <v-select v-model="balise.commercialPackageId" :options="commercialPackages" label="name"
                                :reduce="pkg => pkg.id" :disabled="isUpdating || isDeleting" required />
                        </div>
                    </div>

                    <!-- Icône -->
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <label for="icon" class="form-label fw-bold">Icône</label>
                            <div class="col d-flex justify-content-center">
                                <img :src="`/img/cars/${balise.icon}`" type="text"
                                    class="img-fluid border rounded pointer-cursor" @click="baliseToUpdate = balise.id"
                                    data-bs-toggle="modal" data-bs-target="#iconPicker" />
                            </div>
                        </div>
                    </div>

                    <!-- Options -->
                    <div class="row mb-3">
                        <div class="col-md-4">
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="balise.remoteMonitoring"
                                    id="remoteMonitoring" :disabled="isUpdating || isDeleting" />
                                <label class="form-check-label fw-bold" for="remoteMonitoring">
                                    Télésurveillance
                                </label>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="balise.hasTag" id="hasTag"
                                    :disabled="isUpdating || isDeleting" />
                                <label class="form-check-label fw-bold" for="hasTag">
                                    Possède un Badge ?
                                </label>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="balise.free" id="free"
                                    :disabled="isUpdating || isDeleting" />
                                <label class="form-check-label fw-bold" for="free">
                                    Non facturable
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Date de mise en service et Identifiant Atlant -->
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <label for="dateService" class="form-label fw-bold">Date de mise en service</label>
                            <input v-model="balise.serviceDate" type="date" class="form-control" id="dateService"
                                :disabled="isUpdating || isDeleting" />
                        </div>
                        <div class="col-md-6">
                            <label for="atlantId" class="form-label fw-bold">Identifiant Atlant</label>
                            <input v-model="balise.atlantId" type="text" class="form-control"
                                :class="{ 'locked-field': clientLocked }" id="atlantId" placeholder="Code Atlant"
                                :disabled="isUpdating || isDeleting || clientLocked" />
                            <small v-if="isClientLocked" class="text-danger fst-italic">
                                Champ verrouillé car le client est verrouillé
                            </small>
                        </div>
                    </div>

                    <!-- Boutons -->
                    <div class="d-flex flex-column align-items-center">
                        <button type="submit" class="btn btn-success mb-2"
                            :disabled="isUpdating || isDeleting || !hasUnsavedChanges">
                            <span v-if="isUpdating" class="spinner-border spinner-border-sm me-2"></span>
                            Enregistrer les modifications
                        </button>
                        <p v-if="updateMessage" class="text-center"
                            :class="{ 'text-success': updateSuccess, 'text-danger': !updateSuccess }">
                            {{ updateMessage }}
                        </p>
                    </div>
                </form>

                <!-- Séparateur visuel entre infos balise et commandes/boutons -->
                <hr class="my-5" />
                <h5 class="fw-bold mb-3 text-primary">
                    Configuration des commandes et boutons
                </h5>

                <!-- Commandes enregistrées pour ce type de balise -->
                <div class="row mb-4">
                    <div class="col-12">
                        <h5 class="fw-bold">Commandes enregistrées pour ce type de balise</h5>
                        <p v-if="!balise.type" class="text-muted fst-italic">
                            Sélectionnez d'abord un type de balise pour gérer les commandes enregistrées.
                        </p>
                        <div v-else>
                            <table class="table table-sm align-middle">
                                <thead>
                                    <tr>
                                        <th>Nom</th>
                                        <th>Commande</th>
                                        <th style="width: 120px;"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="!commandsForType.length">
                                        <td colspan="3" class="text-muted fst-italic">
                                            Aucune commande enregistrée pour ce type de balise.
                                        </td>
                                    </tr>
                                    <tr v-for="cmd in commandsForType" :key="cmd.id">
                                        <td>{{ cmd.name || '-' }}</td>
                                        <td><code>{{ cmd.cmd }}</code></td>
                                        <td class="text-end">
                                            <button type="button" class="btn btn-sm btn-outline-danger"
                                                @click="deleteRegisteredCommand(cmd.id)"
                                                :disabled="isUpdating || isDeleting || isSavingCommand">
                                                Supprimer
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div class="border rounded p-3">
                                <h6 class="fw-bold mb-3">Ajouter une commande enregistrée</h6>
                                <div class="row g-2">
                                    <div class="col-md-4">
                                        <input v-model="newCommand.name" type="text" class="form-control"
                                            placeholder="Nom lisible (ex. Ouvrir porte)" />
                                    </div>
                                    <div class="col-md-6">
                                        <input v-model="newCommand.cmd" type="text" class="form-control"
                                            placeholder="Commande brute à envoyer" />
                                    </div>
                                    <div class="col-md-2 d-grid">
                                        <button type="button" class="btn btn-outline-primary"
                                            @click="createRegisteredCommand"
                                            :disabled="isUpdating || isDeleting || isSavingCommand">
                                            <span v-if="isSavingCommand"
                                                class="spinner-border spinner-border-sm me-1" />
                                            Ajouter
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Boutons liés à cette balise -->
                <div class="row mb-4">
                    <div class="col-12">
                        <h5 class="fw-bold">Boutons liés à cette balise</h5>
                        <p class="text-muted fst-italic">
                            Associez un index de bouton à une commande enregistrée pour ce type de balise.
                        </p>

                        <table class="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th>Index</th>
                                    <th>Libellé</th>
                                    <th>Commande associée</th>
                                    <th style="width: 120px;"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="!baliseButtons.length">
                                    <td colspan="4" class="text-muted fst-italic">
                                        Aucun bouton configuré pour cette balise.
                                    </td>
                                </tr>
                                <tr v-for="(button, index) in baliseButtons" :key="button.id || button.buttonIndex">
                                    <td>{{ button.buttonIndex }}</td>
                                    <td>{{ button.label || '-' }}</td>
                                    <td>{{ getCommandName(button.baliseCmdRegisteredId) }}</td>
                                    <td class="text-end">
                                        <button type="button" class="btn btn-sm btn-outline-danger"
                                            @click="removeButton(index)"
                                            :disabled="isUpdating || isDeleting || isSavingButtons">
                                            Supprimer
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div class="border rounded p-3">
                            <h6 class="fw-bold mb-3">Ajouter un bouton</h6>
                            <div class="row g-2">
                                <div class="col-md-2">
                                    <input v-model.number="newButton.buttonIndex" type="number" min="0"
                                        class="form-control" placeholder="Index" />
                                </div>
                                <div class="col-md-4">
                                    <input v-model="newButton.label" type="text" class="form-control"
                                        placeholder="Libellé (optionnel)" />
                                </div>
                                <div class="col-md-4">
                                    <v-select v-model="newButton.baliseCmdRegisteredId" :options="commandsForType"
                                        label="name" :reduce="c => c.id"
                                        :disabled="!commandsForType.length || isUpdating || isDeleting || isSavingButtons"
                                        placeholder="Commande associée" />
                                </div>
                                <div class="col-md-2 d-grid">
                                    <button type="button" class="btn btn-outline-primary" @click="addButton"
                                        :disabled="isUpdating || isDeleting || isSavingButtons || !commandsForType.length">
                                        <span v-if="isSavingButtons" class="spinner-border spinner-border-sm me-1" />
                                        Ajouter
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Zone spéciale suppression historique -->
            <div class="card shadow mx-3 mt-4 border-danger">
                <div class="card-header bg-danger text-white rounded-0 rounded-top">
                    Action sensible
                </div>
                <div class="card-body">
                    <p class="text-muted">
                        La suppression de l’historique efface <strong>toutes les données de trajets, positions et
                            alertes</strong>
                        enregistrées pour cette balise.
                        ⚠️ À utiliser uniquement après les tests internes, avant livraison au client.
                    </p>
                    <div class="d-flex justify-content-center">
                        <button type="button" class="btn btn-danger" @click="deleteHistory"
                            :disabled="!canDeleteHistory || isDeleting || deleteMessage != ''">
                            <span v-if="isDeleting" class="spinner-border spinner-border-sm me-2"></span>
                            Supprimer l’historique
                        </button>
                    </div>
                    <p v-if="deleteMessage" class="text-center mt-2"
                        :class="{ 'text-success': deleteSuccess, 'text-danger': !deleteSuccess }">
                        {{ deleteMessage }}
                    </p>

                    <!-- Messages d’avertissement -->
                    <div v-if="balise" class="mt-2 text-center">
                        <p v-if="balise.clientId !== 11" class="text-danger fst-italic">
                            Vous ne pouvez pas supprimer l’historique d’une balise appartenant à un client autre que
                            <strong>« En attente »</strong>.
                        </p>
                        <p v-else-if="hasUnsavedChanges" class="text-danger fst-italic">
                            Vous devez d’abord enregistrer vos modifications avant de pouvoir supprimer l’historique.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <IconPickerModal :baliseToUpdate="baliseToUpdate" @icon-updated="refreshData" />
    <DefaultFooter />
</template>

<script setup>
import { ref, onMounted, computed } from "vue"
import { useRoute } from "vue-router"
import baliseService from "@/services/balise.service.js"
import ClientService from "@/services/client.service.js"
import commercialPackageService from "@/services/commercialPackage.service"
import CmdService from "@/services/cmd.service.js"
import DefaultHeader from "@/components/DefaultHeader.vue"
import DefaultFooter from "@/components/DefaultFooter.vue"
import IconPickerModal from "@/components/balisesPage/IconPickerModal.vue"

const route = useRoute()

const balise = ref(null)
const originalBalise = ref(null)
const baliseToUpdate = ref(null)
const clients = ref([])
const clientLocked = ref(false)
const isClientLocked = computed(() => clientLocked.value)
const commercialPackages = ref([])

const registeredCommands = ref([])
const baliseButtons = ref([])
const newCommand = ref({ name: "", cmd: "" })
const newButton = ref({ buttonIndex: null, label: "", baliseCmdRegisteredId: null })
const isSavingCommand = ref(false)
const isSavingButtons = ref(false)

const isUpdating = ref(false)
const updateMessage = ref("")
const updateSuccess = ref(true)

const isDeleting = ref(false)
const deleteMessage = ref("")
const deleteSuccess = ref(true)

const baliseTypes = {
    TLW2: "GEOTRACK W2",
    PIONEERX100: "GEOTRACK P100",
    FMB920: "GEOTRACK B92",
    FMC920: "GEOTRACK C92",
    FMC130: "GEOTRACK C13",
    KNIGHTX100: "GEOTRACK X100",
    FMC800: "GEOBD 2",
    TWIGONE: "TWIG ONE",
    TT10: "TT10",
    TLW212B: "GEO TRAILER",
    LL301: "GEONEO",
    NS10: "NS10",
    NS20: "NS20",
}

const portsByBaliseType = {
    TLW2: 25152,
    PIONEERX100: 25150,
    FMB920: 25130,
    FMC920: 25131,
    FMC130: 25132,
    KNIGHTX100: 25151,
    FMC800: 25135,
    TWIGONE: 26000,
    TT10: 25137,
    TLW212B: 25138,
    NS10: "SRTECH",
    NS20: "SRTECH",
}


const ipAddressBaliseManager = "tcp.argos-geoloc.com"

const baliseServerInfo = computed(() => {
    if (!balise.value?.type) return null
    const port = portsByBaliseType[balise.value.type]
    return `${ipAddressBaliseManager}:${port}`
})


const baliseOptions = computed(() =>
    Object.entries(baliseTypes).map(([value, label]) => ({ value, label }))
)

const commandsForType = computed(() => {
    if (!balise.value?.type) return []
    if (!Array.isArray(registeredCommands.value)) return []
    return registeredCommands.value.filter(c => c.baliseType === balise.value.type)
})

//const isEditable = computed(() => balise.value?.clientId === 11)

onMounted(async () => {
    try {
        const response = await baliseService.getBaliseInfo(route.params.id)
        balise.value = response.data
        originalBalise.value = JSON.parse(JSON.stringify(response.data))
        console.log("Balise data loaded:", balise.value)

        if (balise.value?.serviceDate) {
            const formattedDate = balise.value.serviceDate.split('T')[0]
            balise.value.serviceDate = formattedDate
            originalBalise.value.serviceDate = formattedDate
        }

        // Clients
        const clientResponse = await ClientService.getClients()
        clients.value = clientResponse.data

        // Forfaits commerciaux
        const packageResponse = await commercialPackageService.getCommercialPackages()
        commercialPackages.value = packageResponse

        if (balise.value.commercialPackage) {
            balise.value.commercialPackageId = balise.value.commercialPackage.id
        }

        const clientInfo = await ClientService.getClientInfo(balise.value.clientId)
        console.log("Balise's clientId:", balise.value.clientId)
        console.log("Client info:", clientInfo.data)
        clientLocked.value = clientInfo.data.locked;
        await loadCommandsAndButtons()
    } catch (err) {
        console.error("Erreur lors du chargement :", err)
    }
})

const hasUnsavedChanges = computed(() => {
    return JSON.stringify(balise.value) !== JSON.stringify(originalBalise.value)
})

const canDeleteHistory = computed(() => {
    return !hasUnsavedChanges.value && balise.value?.clientId === 11
})

const updateBalise = async () => {
    try {
        isUpdating.value = true
        updateMessage.value = ""
        await baliseService.updateData(route.params.id, balise.value)
        updateMessage.value = "Balise mise à jour avec succès ✅"
        setTimeout(() => {
            updateMessage.value = ""
        }, 3000)
        updateSuccess.value = true
        originalBalise.value = JSON.parse(JSON.stringify(balise.value))
    } catch (err) {
        console.error(err)
        updateMessage.value = "Erreur lors de la mise à jour de la balise ❌"
        setTimeout(() => {
            updateMessage.value = ""
        }, 3000)
        updateSuccess.value = false
    } finally {
        isUpdating.value = false
    }
}

const deleteHistory = async () => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer l'historique de cette balise ?")) return
    try {
        isDeleting.value = true
        deleteMessage.value = ""
        await baliseService.deleteHistory(route.params.id)
        deleteMessage.value = "Historique supprimé avec succès ✅"
        setTimeout(() => {
            deleteMessage.value = ""
        }, 3000)
        deleteSuccess.value = true
    } catch (err) {
        console.error(err)
        deleteMessage.value = "Erreur lors de la suppression de l'historique ❌"
        setTimeout(() => {
            deleteMessage.value = ""
        }, 3000)
        deleteSuccess.value = false
    } finally {
        isDeleting.value = false
    }
}

const refreshData = async (chosenIcon) => {
    balise.value.icon = chosenIcon
}

const getCommandName = (id) => {
    const cmd = registeredCommands.value.find(c => c.id === id)
    return cmd ? (cmd.name || cmd.cmd) : "—"
}

const loadCommandsAndButtons = async () => {
    if (!balise.value) return
    try {
        const registeredRes = await CmdService.getRegistered()
        const buttonsRes = await CmdService.getButtons(route.params.id)

        const regData = registeredRes.data
        const btnData = buttonsRes.data

        // Sécuriser le format des commandes enregistrées
        if (Array.isArray(regData)) {
            registeredCommands.value = regData
        } else if (Array.isArray(regData.rows)) {
            registeredCommands.value = regData.rows
        } else if (Array.isArray(regData.registeredCommands)) {
            registeredCommands.value = regData.registeredCommands
        } else {
            console.error("Format inattendu pour getRegistered :", regData)
            registeredCommands.value = []
        }

        // Sécuriser le format des boutons
        if (Array.isArray(btnData)) {
            baliseButtons.value = btnData
        } else if (Array.isArray(btnData.buttons)) {
            baliseButtons.value = btnData.buttons
        } else {
            console.error("Format inattendu pour getButtons :", btnData)
            baliseButtons.value = []
        }
    } catch (err) {
        console.error("Erreur lors du chargement des commandes/boutons :", err)
    }
}

const createRegisteredCommand = async () => {
    if (!balise.value?.type) {
        alert("Veuillez d'abord sélectionner un type de balise.")
        return
    }
    if (!newCommand.value.name || !newCommand.value.cmd) {
        alert("Veuillez renseigner un nom et une commande.")
        return
    }

    try {
        isSavingCommand.value = true

        await CmdService.addCmdRegistered({
            name: newCommand.value.name,
            cmd: newCommand.value.cmd,
            baliseType: balise.value.type
        })

        newCommand.value = { name: "", cmd: "" }
        await loadCommandsAndButtons()
    } catch (err) {
        console.error("Erreur lors de la création de la commande enregistrée :", err)
        alert("Erreur lors de la création de la commande enregistrée.")
    } finally {
        isSavingCommand.value = false
    }
}

const deleteRegisteredCommand = async (cmdId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette commande ?")) return
    try {
        isSavingCommand.value = true
        await CmdService.deleteCmdRegistered(cmdId)
        await loadCommandsAndButtons()
    } catch (err) {
        console.error("Erreur lors de la suppression de la commande enregistrée :", err)
        alert("Erreur lors de la suppression de la commande enregistrée.")
    } finally {
        isSavingCommand.value = false
    }
}

const saveButtons = async () => {
    if (!balise.value) return
    try {
        isSavingButtons.value = true
        const res = await CmdService.setButtons(route.params.id, baliseButtons.value)
        const saved = res.data ?? res
        if (Array.isArray(saved)) {
            baliseButtons.value = saved
        } else if (Array.isArray(saved.buttons)) {
            baliseButtons.value = saved.buttons
        } else {
            console.error("Format inattendu pour setButtons :", saved)
        }
    } catch (err) {
        console.error("Erreur lors de l'enregistrement des boutons :", err)
        alert("Erreur lors de l'enregistrement des boutons.")
    } finally {
        isSavingButtons.value = false
    }
}

const addButton = async () => {
    if (newButton.value.buttonIndex == null || !newButton.value.baliseCmdRegisteredId) {
        alert("Veuillez renseigner un index de bouton et une commande associée.")
        return
    }
    if (baliseButtons.value.some(b => b.buttonIndex === newButton.value.buttonIndex)) {
        alert("Un bouton existe déjà avec cet index.")
        return
    }
    baliseButtons.value.push({
        buttonIndex: newButton.value.buttonIndex,
        label: newButton.value.label || null,
        baliseCmdRegisteredId: newButton.value.baliseCmdRegisteredId
    })
    newButton.value = { buttonIndex: null, label: "", baliseCmdRegisteredId: null }
    await saveButtons()
}

const removeButton = async (index) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce bouton ?")) return
    baliseButtons.value.splice(index, 1)
    await saveButtons()
    await loadCommandsAndButtons()
}
</script>

<style scoped>
.card-header {
    border-top-left-radius: 1rem !important;
    border-top-right-radius: 1rem !important;
}

/* Overlay global pour bloquer les interactions */
.overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
    flex-direction: column;
    text-align: center;
}

.spinner-border-lg {
    width: 4rem;
    height: 4rem;
}

.locked-field {
    background-color: #f8f9fa;
    cursor: not-allowed;
}
</style>
